import React, { createContext, useContext, useEffect, useState } from 'react';
import api from '../api/axios';
import { paymentsApi } from '../api/payments';

const SubscriptionContext = createContext();

export const useSubscription = () => {
    return useContext(SubscriptionContext);
};

export const SubscriptionProvider = ({ children }) => {
    const [subscriptions, setSubscriptions] = useState([]);
    const [installments, setInstallments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const apiRequest = async (apiFunc, context = 'API Request') => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiFunc();
            return response.data;
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Unknown error occurred';
            console.error(`${context}:`, errorMessage);
            setError(errorMessage);
            return null;
        } finally {
            setLoading(false);
        }
    };

    const fetchSubscriptions = async () => {
        const data = await apiRequest(
            () => api.get(paymentsApi.subscriptionCatalog),
            'Fetch subscription catalog'
        );
        const list = data?.data;
        if (Array.isArray(list)) {
            setSubscriptions(list);
        } else {
            setSubscriptions([]);
        }
    };

    const fetchInstallments = async (courseId, options = {}) => {
        if (!courseId) {
            setError('Course ID is required to fetch installments');
            return;
        }
        const { planType, userId, installmentPlanId } = options;
        let queryString = '';
        if (planType || userId || installmentPlanId) {
            const params = new URLSearchParams();
            if (planType) params.append('planType', planType);
            if (userId) params.append('userId', userId);
            if (installmentPlanId) params.append('installmentPlanId', installmentPlanId);
            queryString = `?${params.toString()}`;
        }
        const data = await apiRequest(
            () => api.get(`${paymentsApi.installmentPlans(courseId)}${queryString}`),
            `Fetch installment plans ${courseId}`
        );
        const list = data?.data;
        if (Array.isArray(list)) {
            setInstallments(list);
        } else {
            setInstallments([]);
        }
    };

    useEffect(() => {
        fetchSubscriptions();
    }, []);

    const value = {
        subscriptions,
        installments,
        loading,
        error,
        fetchSubscriptions,
        fetchInstallments,
    };

    return (
        <SubscriptionContext.Provider value={value}>
            {children}
        </SubscriptionContext.Provider>
    );
};
